import {
    CalendarIcon,
    CircleExclamationIcon,
    ClockIcon,
    ErrorMessage,
    LocationIcon,
    style,
    TagIcon,
    Tooltip,
    UserCheckIcon,
    UserIcon,
    UsersGroupIcon,
    UserTwoIcon,
} from "@r360/library";
import { max, min } from "lodash";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { ActivityDesktop } from "../../assets/reservationTypes/Activity";
import { ExternalProducts } from "../../components/ExternalProducts";
import { NoSearchResult } from "../../components/NoSearchResult/NoSearchResult";
import { ProductCardSkeleton } from "../../components/ProductCard";
import { ProductCard } from "../../components/ProductCard/ProductCard";
import { ReservationTypeHeader } from "../../components/ReservationTypeHeader/ReservationTypeHeader";
import { ReservationTypeImage } from "../../components/ReservationTypeImage/ReservationTypeImage";
import { RichText } from "../../components/UI";
import { capitalizeFirstLetter, formatPrice, getCanonicalLink, getDynamicPath } from "../../Helper";
import { newFilterGroupItems } from "../../hooks";
import useAppSelector from "../../hooks/useAppSelector";
import useChangeFilter from "../../hooks/useChangeFilter";
import useChangeSearch from "../../hooks/useChangeSearch";
import { useProductsNotAddedPath } from "../../hooks/useProductsNotAddedPath";
import useTranslate from "../../hooks/useTranslate";
import { requestLoadingByResvTypeSelector, resvTypeByLid } from "../../Selectors";
import { currencySelector, externalProductsSelector, reservationTypeResultSelector } from "../../selectors/Selectors";
import { TCriteriaItem, TGroup, TItem, TReservationType, TReservationTypeLid } from "../../store/types";
import { RootState } from "../..";
import { Helmet } from "react-helmet";
import { usePathBuilder } from "../../hooks/usePathBuilder";

type TActivitiesPage = {
    reservationTypeid: TReservationTypeLid;
};

export const ActivitiesPage = ({ reservationTypeid }: TActivitiesPage) => {
    const t = useTranslate();
    const { buildPath } = usePathBuilder();
    const filter = useAppSelector(state => state.filter);
    const activeFilters = filter[reservationTypeid];
    const { isDesktopNewDesign } = useAppSelector(state => state.window);
    const isLoading = useAppSelector(state =>
        requestLoadingByResvTypeSelector(state, "fetchReservationTypeResults", reservationTypeid)
    );
    const currency = useAppSelector(currencySelector);
    const activitiesResult = useAppSelector(state => reservationTypeResultSelector(state, reservationTypeid));
    const reservationType: TReservationType = useAppSelector(state => resvTypeByLid(state, reservationTypeid));
    const reservationTypeCriterias = reservationType?.criterias || {}; // eslint-disable-line
    const externalProductsPosition = reservationType?.externalProductsPosition;
    const externalProducts = useAppSelector(externalProductsSelector)?.filter(
        product => product.reservation_type_id === reservationTypeid
    );
    const channellidFromSearch = activitiesResult?.search?.channellid;
    const filteredExternalProducts = externalProducts?.filter(
        product => product.channellid === 0 || product.channellid === channellidFromSearch
    );
    const [filteredData, setFilteredData] = useState<TGroup[]>([]);
    const [addToCartDataQuantity, setAddToCartDataQuantity] = useState<{ [key: number]: number }>({});
    const addToCartTotalQuantity = Object.values(addToCartDataQuantity).reduce((acc, item) => acc + item, 0);
    const showProductsNotAddedWarning = !!addToCartTotalQuantity;
    const clientData = useAppSelector(state => state.clientData);
    const showDefaultPools: boolean = clientData.reservationtypes.find(
        (resType: TReservationType) => resType.id === reservationTypeid
    ).useShowDefaultPools;

    const seoTitle =
        clientData.reservationtypes.find((resType: TReservationType) => resType.id === reservationTypeid).seoTitle ||
        clientData.reservationtypes.find((resType: TReservationType) => resType.id === reservationTypeid).description;
    const seoDescription = clientData.reservationtypes.find(
        (resType: TReservationType) => resType.id === reservationTypeid
    ).seoDescription;
    const titleSuffix = clientData?.siteMeta?.seoTitleSuffix || "";
    const seoImage = clientData.reservationtypes
        .find((resType: TReservationType) => resType.id === reservationTypeid)
        .desktopImage?.path?.replace("_org", "_800w");
    const canonicalLink = reservationTypeid && getCanonicalLink(buildPath(reservationTypeid));

    useChangeSearch({
        reservationResultSearch: activitiesResult?.search,
        reservationTypeId: reservationTypeid,
        skipGuestsOnSearchWithPrice: true,
        allowSearchWithoutPrice: !!showDefaultPools,
    });
    useProductsNotAddedPath(showProductsNotAddedWarning);
    useChangeFilter(reservationTypeid);

    // Clear addToCartData when a new search has been made.
    useEffect(() => {
        setAddToCartDataQuantity({});
    }, [activitiesResult?.resultSetId]);

    useEffect(() => {
        const criterias = Object.values(reservationTypeCriterias)
            .flat()
            .reduce((acc, curr) => {
                acc[curr.code] = curr;
                return acc;
            }, {} as { [key: number]: TCriteriaItem });

        // Filter by active criterias.
        const filteredData = Object.values(activitiesResult?.groups || {})
            .map((group: TGroup) => {
                return newFilterGroupItems(group, activeFilters || {}, criterias);
            })
            .filter(group => !!group.items.length);

        setFilteredData(filteredData);
    }, [activitiesResult?.groups, activeFilters, reservationTypeCriterias]);

    const getGroupAgeSpan = (group: TGroup) => {
        const { minAge, maxAge } = group.items.reduce(
            (acc, curr) => {
                return {
                    minAge: !acc.minAge ? curr.minage : min([curr.minage, acc.minAge]) ?? 0,
                    maxAge: max([curr.maxage, acc.maxAge]) ?? 0,
                };
            },
            { minAge: 0, maxAge: 0 }
        );

        if (minAge === 0 && maxAge === 99) {
            return t("book.general.all_ages");
        } else {
            return `${minAge} - ${maxAge} ${t("book.general.years")}`;
        }
    };

    const getItemColumns = (item: TItem) => {
        const occations = item.occations;
        const multipleOccations = occations.length > 1;
        const firstOccation = occations[0];
        const lastOccation = multipleOccations ? occations[occations.length - 1] : undefined;
        const days = moment(lastOccation?.date).diff(moment(firstOccation.date), "days") + 1;
        const daysWithoutOccation = days - occations.length;
        const bookedPlaces = parseInt(item.booked ?? "0");
        const unlimitedPlaces = item.freesale === "F" ? false : !!item.freesale;
        const privateActivity = item.limittoonereservation === "F" ? false : !!item.limittoonereservation;
        const privateActivitySingle = bookedPlaces + (item?.available ?? 0) === 1;

        const lowestPrice = (item: TItem) => {
            const priceEntries = Object.entries(item).filter(
                ([key, value]) => key.startsWith("pricec") && typeof value === "number"
            );

            if (priceEntries.length === 0) {
                return null;
            } else if (priceEntries) {
                return priceEntries.reduce(
                    (min, [, value]) => (value && min ? (value < min ? value : min) : null),
                    priceEntries[0][1]
                );
            }
        };

        return [
            {
                title: (
                    <div className="u-d-flex u-gap-6 u-align-items-center" style={{ minWidth: 185 }}>
                        {multipleOccations ? (
                            <div>
                                <div>
                                    <span>
                                        {`${moment(firstOccation.date).format("D MMMM")} - ${moment(
                                            lastOccation?.date
                                        ).format("D MMMM")}`}
                                    </span>
                                </div>
                                <div>
                                    <span>{t("book.general.number_days", [occations.length])}</span>
                                </div>
                            </div>
                        ) : (
                            <span>{moment(firstOccation?.date).format("D MMMM")}</span>
                        )}
                        {daysWithoutOccation > 0 && (
                            <Tooltip
                                toolTipContent={
                                    <>
                                        {occations.map(occ => {
                                            return (
                                                <div key={occ.date}>
                                                    <span>
                                                        {capitalizeFirstLetter(moment(occ.date).format("dddd D MMMM"))}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </>
                                }
                                triggerOnClick
                            >
                                <div style={{ cursor: "pointer" }}>
                                    <CircleExclamationIcon color={style.yellowColor} />
                                </div>
                            </Tooltip>
                        )}
                    </div>
                ),
                icon: <CalendarIcon color={style.brandBlueColor} />,
            },
            {
                title: <span>{`${item.fromtime} - ${item.totime}`}</span>,
                icon: <ClockIcon color={style.brandBlueColor} />,
            },
            {
                title: <span>{item.location ? capitalizeFirstLetter(item.location.toLowerCase()) : undefined}</span>,
                icon: <LocationIcon color={style.brandBlueColor} />,
            },
            {
                title: (
                    <>
                        {privateActivitySingle ? (
                            <>
                                <RichText content={t("book.activity.activity_for_one")} />
                            </>
                        ) : privateActivity ? (
                            <>
                                <RichText
                                    content={t("book.activity.company_max_persons", [
                                        bookedPlaces + (item.available ?? 0),
                                    ])}
                                />
                            </>
                        ) : (
                            <>
                                <span>
                                    {unlimitedPlaces
                                        ? t("book.activity.unlimited_places")
                                        : t("book.activity.group_activity")}
                                </span>
                                {!unlimitedPlaces && (
                                    <span>
                                        {t("book.activity.places_of_places_left", [
                                            item.available ?? 0,
                                            bookedPlaces + (item.available ?? 0),
                                        ])}
                                    </span>
                                )}
                            </>
                        )}
                    </>
                ),
                icon: privateActivitySingle ? (
                    <UserIcon color={style.brandBlueColor} />
                ) : privateActivity ? (
                    <UserTwoIcon color={style.brandBlueColor} />
                ) : (
                    <UsersGroupIcon color={style.brandBlueColor} />
                ),
            },
            {
                title: <span>{`${formatPrice(lowestPrice(item), currency)}`}</span>,
                icon: <TagIcon color={style.brandBlueColor} />,
            },
        ];
    };

    const renderActivitiesList = () => {
        if (isLoading) {
            return [...Array(8)].map((_, i) => <ProductCardSkeleton key={i} isDesktop={isDesktopNewDesign} />);
        }

        if (filteredData.length > 0 && !isLoading) {
            return filteredData.map(group => {
                const title = group.groupitem?.title ?? group.items[0]?.title;
                const images = [
                    ...(group.items[0]?.mainimage ? [group.items[0]?.mainimage] : []),
                    ...(group.items[0]?.images || []),
                ];

                return (
                    <ProductCard
                        key={group.id}
                        group={group}
                        title={title}
                        description={group.groupitem?.weblong ?? group.items[0]?.weblong}
                        images={images}
                        currency={currency}
                        getItemColumns={getItemColumns}
                        showItemsButtonText={t("book.activity.show_occurrences", [group.items.length])}
                        fallbackImage={getDynamicPath(ActivityDesktop)}
                        renderAdditionalInformation={() => {
                            return (
                                <div className="u-d-flex u-gap-6 u-align-items-center">
                                    <UserCheckIcon />
                                    <span className="u-fw-medium">{getGroupAgeSpan(group)}</span>
                                </div>
                            );
                        }}
                        onAddToCartQuantityChange={data =>
                            setAddToCartDataQuantity(prevState => {
                                if (prevState[group.items[0]?.id] === data) {
                                    return prevState;
                                }

                                return { ...prevState, [group.items[0]?.id]: data };
                            })
                        }
                        decorateItemBeforeCart={item => ({ ...item, groupTitle: title })}
                        reservationType={reservationType.type}
                        searchWithoutPrice={showDefaultPools && !activitiesResult?.search.checkPriceAndAvailability}
                        hideItemsButton={showDefaultPools && !activitiesResult?.search.checkPriceAndAvailability}
                    />
                );
            });
        }

        if (
            (activitiesResult?.hitsMatchingFilter === "0" || !filteredData.length) &&
            !isLoading &&
            filteredExternalProducts.length === 0
        ) {
            return <NoSearchResult />;
        }

        if (activitiesResult?.error && !isLoading) {
            return (
                <div className="u-pt-24">
                    <ErrorMessage
                        headingText={t("book.general.error")}
                        primaryButtonText={t("book.general.reload_page")}
                        primaryButtonOnClick={() => window.location.reload()}
                    />
                </div>
            );
        }

        return null;
    };

    const showFilter =
        activitiesResult?.groups &&
        reservationTypeCriterias &&
        Object.values(activitiesResult.groups).length > 0 &&
        Object.values(reservationTypeCriterias).flat().length > 0;

    return activitiesResult?.search || isLoading ? (
        <section>
            <Helmet>
                <title>
                    {seoTitle} | {titleSuffix}
                </title>
                <meta name="description" content={seoDescription} />
                <meta property="og:title" content={`${seoTitle} | ${titleSuffix}`} />
                <meta property="og:image" content={seoImage} />
                <meta property="og:description" content={seoDescription} />
                <link rel="canonical" href={canonicalLink.toString()} />
            </Helmet>
            <ReservationTypeHeader
                reservationTypeId={reservationTypeid}
                hits={filteredData.length + filteredExternalProducts.length}
                filtersAvailable={showFilter}
            />
            {externalProductsPosition === "first" && (
                <ExternalProducts
                    show={!isLoading}
                    reservationTypeId={reservationTypeid}
                    externalProducts={filteredExternalProducts}
                    reservationResult={activitiesResult}
                />
            )}
            {renderActivitiesList()}
            {externalProductsPosition === "last" && (
                <ExternalProducts
                    show={!isLoading}
                    reservationTypeId={reservationTypeid}
                    externalProducts={filteredExternalProducts}
                    reservationResult={activitiesResult}
                />
            )}
        </section>
    ) : (
        <>
            <Helmet>
                <title>
                    {seoTitle} | {titleSuffix}
                </title>
                <meta name="description" content={seoDescription} />
                <meta property="og:title" content={`${seoTitle} | ${titleSuffix}`} />
                <meta property="og:image" content={seoImage} />
                <meta property="og:description" content={seoDescription} />
                <link rel="canonical" href={canonicalLink.toString()} />
            </Helmet>
            <ReservationTypeImage reservationTypeId={reservationTypeid} />
        </>
    );
};
